import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MaskedField } from "../components/MaskedField";
import { Users, CreditCard, KeyRound, Wallet, ArrowLeft, Plus, Search, Trash2, Activity, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const PAGE_SIZE = 8;

export default function AdminMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [vault, setVault] = useState(null);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", password: "", name: "", company: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reportMonth, setReportMonth] = useState("");

  const loadMerchants = (p = page, q = search) =>
    api
      .get("/admin/merchants", { params: { page: p, page_size: PAGE_SIZE, q } })
      .then((res) => {
        setMerchants(res.data.merchants);
        setTotal(res.data.total);
      })
      .catch(() => {});

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadMerchants(1, search); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => { loadMerchants(page, search); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setC = (k) => (e) => setCreateForm({ ...createForm, [k]: e.target.value });

  const createMerchant = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/merchants", createForm);
      toast.success("Merchant account created");
      setShowCreate(false);
      setCreateForm({ email: "", password: "", name: "", company: "" });
      loadMerchants(1, search);
      setPage(1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const deleteMerchant = async () => {
    try {
      await api.delete(`/admin/merchants/${deleteTarget.id}`);
      toast.success("Merchant and vault data deleted");
      setDeleteTarget(null);
      loadMerchants(page, search);
    } catch {
      toast.error("Could not delete merchant");
    }
  };

  const openMerchant = (m) => {
    setSelected(m);
    setVault(null);
    api.get(`/admin/merchants/${m.id}/vault`).then((res) => setVault(res.data)).catch(() => {});
  };

  const downloadReport = async (m) => {
    try {
      const params = reportMonth ? { month: reportMonth } : {};
      const res = await api.get(`/admin/merchants/${m.id}/report`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `citidan_report_${m.email.split("@")[0]}${reportMonth ? "_" + reportMonth.replace("-", "_") : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Compliance report downloaded");
    } catch {
      toast.error("Could not generate report");
    }
  };

  if (selected) {
    return (
      <div className="space-y-8">
        <button onClick={() => setSelected(null)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to merchants
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-accent">Merchant Oversight</div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight">{selected.company || selected.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{selected.email}</p>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Report month</label>
              <Input
                type="month"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                data-testid="report-month-input"
                className="w-40"
              />
            </div>
            <Button variant="outline" onClick={() => downloadReport(selected)} data-testid="download-report-btn">
              <FileText className="mr-1.5 h-4 w-4" /> {reportMonth ? `${reportMonth} (PDF)` : "This Month (PDF)"}
            </Button>
          </div>
        </div>

        {!vault ? (
          <div className="text-sm text-muted-foreground">Loading vault…</div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6" data-testid="merchant-credential">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-medium"><KeyRound className="h-4 w-4 text-primary" /> API Credentials</h3>
              {vault.credential ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium">{vault.credential.processor}</div>
                  <MaskedField label="API Key" value={vault.credential.api_key_mask} />
                  <MaskedField label="Secret Key" value={vault.credential.secret_key_mask} />
                  <MaskedField label="Endpoint" value={vault.credential.endpoint_url} mono={false} />
                </div>
              ) : <p className="text-sm text-muted-foreground">No credentials stored.</p>}
            </div>

            <div className="rounded-xl border border-border bg-card p-6" data-testid="merchant-cards">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-medium"><CreditCard className="h-4 w-4 text-primary" /> Card Tokens ({vault.cards.length})</h3>
              {vault.cards.length ? (
                <div className="space-y-3">
                  {vault.cards.map((c) => (
                    <div key={c.id} className="rounded-lg border border-border bg-muted/30 p-3">
                      <div className="mb-1 text-xs text-muted-foreground">{c.brand} · {c.holder_name} · {c.expiry}</div>
                      <div className="font-mono text-sm">{c.token}</div>
                      <div className="font-mono masked-dots text-xs text-muted-foreground">{c.card_mask}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No tokens stored.</p>}
            </div>

            <div className="rounded-xl border border-border bg-card p-6" data-testid="merchant-wallet">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-medium"><Wallet className="h-4 w-4 text-primary" /> Crypto Wallet</h3>
              {vault.wallet ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">{vault.wallet.label} · {vault.wallet.chain}</div>
                  <MaskedField label="Private Key" value={vault.wallet.key_mask} />
                </div>
              ) : <p className="text-sm text-muted-foreground">No wallet stored.</p>}
            </div>

            <div className="rounded-xl border border-border bg-card p-6" data-testid="merchant-events">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-medium"><Activity className="h-4 w-4 text-primary" /> Token Activity ({vault.events?.length || 0})</h3>
              {vault.events?.length ? (
                <div className="divide-y divide-border">
                  {vault.events.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                      <div className="min-w-0">
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider">{e.type}</span>
                        <span className="ml-2 font-mono text-xs text-muted-foreground">{e.token.slice(0, 16)}…</span>
                        {e.type === "used" && e.amount != null && <span className="ml-2 text-xs text-accent">${e.amount.toFixed(2)}</span>}
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No token activity.</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-accent">CITIDAN Staff</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Merchant Management</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create merchants and oversee their encrypted vault records.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-merchant-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Create Merchant
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="merchant-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, company or email…"
          className="pl-9"
        />
      </div>

      {merchants.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card py-16 text-center text-sm text-muted-foreground">
          <Users className="h-8 w-8 opacity-40" />
          {search ? `No merchants match “${search}”.` : "No merchants yet. Create one to get started."}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {merchants.map((m) => (
              <div key={m.id} className="rounded-xl border border-border bg-card p-5" data-testid={`merchant-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold uppercase">
                    {m.email[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{m.company || m.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                  </div>
                  <button
                    onClick={() => setDeleteTarget(m)}
                    data-testid={`delete-merchant-${m.id}`}
                    className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    title="Delete merchant"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> {m.token_count} tokens</span>
                  <span className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> {m.has_credentials ? "API set" : "none"}</span>
                  <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> {m.has_wallet ? "wallet" : "none"}</span>
                </div>
                <Button variant="outline" className="mt-4 w-full" onClick={() => openMerchant(m)} data-testid={`view-merchant-${m.id}`}>
                  View vault
                </Button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between" data-testid="merchant-pagination">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="merchants-prev">
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages} · {total} merchants</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="merchants-next">
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-merchant-dialog">
          <DialogHeader>
            <DialogTitle>Create merchant account</DialogTitle>
            <DialogDescription>The merchant can sign in immediately with these credentials.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createMerchant} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="create-name-input" value={createForm.name} onChange={setC("name")} required placeholder="Jane Merchant" />
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input data-testid="create-company-input" value={createForm.company} onChange={setC("company")} placeholder="Acme Inc." />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="create-email-input" type="email" value={createForm.email} onChange={setC("email")} required placeholder="merchant@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <Input data-testid="create-password-input" value={createForm.password} onChange={setC("password")} required placeholder="min 6 characters" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="submit-create-merchant">
                {creating ? "Creating…" : "Create Merchant"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-merchant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.company || deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the merchant and all their encrypted vault data (credentials, tokens, wallet, activity). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMerchant} data-testid="confirm-delete-merchant" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
